import { ProcedureBridgeApi } from 'client/components/schema/procedure-bridge/ProcedureBridgeApi';
import { useEffect, useState } from 'react';
import { RequestResponseFileType } from 'common/schema/request/RequestResponseFileType';
import {
  DocumentDossier,
  IRequestDossierRecord
} from 'common/dto/RequestDossierDto';

export type DocumentTree = ReturnType<typeof useRequestDocumentTree>;
/**
 * Hook per la gestione dell'albero dei documenti con caricamento asincrono.
 */
export function useRequestDocumentTree(requestId: number | undefined | null) {
  const [documents, setDocuments] = useState([] as IRequestDossierRecord[]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);

        if (!requestId) return;

        const documentsResponse = await ProcedureBridgeApi.listRootPublicDocuments(
          { requestId }
        );
        const subdossiersResponse = await ProcedureBridgeApi.listRootSubdossiers(
          { requestId }
        );

        setDocuments([
          ...subdossiersResponse.data.map(dossier => ({
            ...dossier,
            children: []
          })),
          ...documentsResponse.data
        ]);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }

    fetch();
  }, [requestId]);

  // Provvede a caricare il singolo nodo di un documento e dunque i suoi figli.
  // TODO non va sicuramente, la setValue non funziona in questo caso
  async function loadDocument(subdossier: DocumentDossier) {
    try {
      // Se non c'è la richiesta o le subfolder sono già calcolate, non aggiornare.
      if (subdossier.children && subdossier.children.length > 0) return;
      if (!requestId) return;

      // Recupera tutti Documenti e Sottofascicoli
      const documentsList = await ProcedureBridgeApi.listPublicDocuments({
        requestId,
        token: subdossier.token
      });
      const subfolderList = await ProcedureBridgeApi.listSubdossiers({
        requestId,
        token: subdossier.token
      });
      const children: IRequestDossierRecord[] = [
        ...subfolderList.data.map(dossier => ({
          ...dossier,
          children: []
        })),
        ...documentsList.data
      ];

      // Estrae il path dall root contenuto all'interno del token
      const path = subdossier.path;

      // Ricerca il nodo da modificare nell'albero dei documenti
      // (specificatamente un Dossier/Fascicolo)
      let node = documents.filter(
        document => document.type === RequestResponseFileType.Dossier
      );

      path.forEach(dossierId => {
        node =
          (node.find(
            n =>
              n.data.id == dossierId &&
              n.type === RequestResponseFileType.Dossier
          ) as DocumentDossier).children ?? [];
      });

      // Modifica i figli del nodo (Fascicolo) con quelli recuperati dal server
      node
        .filter(n => n.token === subdossier.token)
        .map(n => ((n as DocumentDossier).children = children));

      // Modifica lo stato dei documenti del Tree
      setDocuments(documents);
    } catch (e) {
      setError(e);
    }
  }

  return { loadDocument, documents, loading, error };
}
